import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { POST_CLAIM_CTA_CONFIG } from '../constants/postClaimCta';
import { selectCustomScenarios } from '../store/selectors/init';
import { IPostClaimCta } from '../types/ui';
import { resolvePostClaimCta } from '../util/postClaimCta';

interface IPostClaimCtaState {
  cta: IPostClaimCta | undefined;
  countdownLabel: string;
  redirect: () => void;
}

export const usePostClaimCta = (isClaimSuccessful: boolean): IPostClaimCtaState => {
  const scenarios = useSelector(selectCustomScenarios);

  const cta = useMemo(
    () => (isClaimSuccessful ? resolvePostClaimCta(scenarios, POST_CLAIM_CTA_CONFIG) : undefined),
    [isClaimSuccessful, scenarios],
  );

  const [secondsLeft, setSecondsLeft] = useState(() => cta?.countdownSeconds ?? 0);

  const redirect = useCallback(() => {
    if (cta) {
      window.location.href = cta.url;
    }
  }, [cta]);

  useEffect(() => {
    if (!cta) {
      return;
    }

    setSecondsLeft(cta.countdownSeconds);

    const timeout = setTimeout(redirect, cta.countdownSeconds * 1000);
    const interval = setInterval(() => setSecondsLeft((prev) => Math.max(0, prev - 1)), 1000);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [cta, redirect]);

  const countdownLabel = useMemo(
    () => `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`,
    [secondsLeft],
  );

  return { cta, countdownLabel, redirect };
};
